import {LatLngLiteral} from 'leaflet';
import {CompetitorType, TrackPoint, SiDataType} from 'providers/EventProvider';
import {ControlType} from 'providers/SplitProvider';

const MAX_RADIUS = 30;
const MAX_TIME = 60000;

const vectorLen = (p1: TrackPoint) => {
  return Math.sqrt(p1.lat * p1.lat + p1.lng * p1.lng);
};

const toVector = (p1: TrackPoint, p2: TrackPoint) => {
  return {
    lng: p2.lng - p1.lng,
    lat: p2.lat - p1.lat,
    time: p2.time - p1.time,
    alt: (p2.alt ?? 0) - (p1.alt ?? 0)
  };
};

const transform = (vec: TrackPoint, k: number) => {
  return {
    lng: vec.lng * k,
    lat: vec.lat * k,
    time: vec.time * k,
    alt: (vec.alt ?? 0) * k
  };
};
const plus = (point: TrackPoint, vec: TrackPoint) => {
  return {
    lng: point.lng + vec.lng,
    lat: point.lat + vec.lat,
    time: point.time + vec.time,
    alt: (point.alt ?? 0) + (vec.alt ?? 0)
  };
};

const interpolate = (first: TrackPoint, last: TrackPoint) => {
  const timeDiff = (last.time - first.time) / 1000;
  const result = [];
  if (timeDiff > 1) {
    const vector = toVector(first, last);
    for (let i = 1; i < timeDiff; i++) {
      result.push(plus(first, transform(vector, i / timeDiff)));
    }
  }
  result.push(last);
  return result;
};

const interpolationPlayerTrack = (data: TrackPoint[]) => {
  const result = [];
  result.push(data[0]);

  for (let i = 1; i < data.length; i++) {
    const points = interpolate(data[i - 1], data[i]);
    result.push(...points);
  }

  return result;
};

const dist = (v1: TrackPoint, v2: TrackPoint) => {
  return vectorLen(toVector(v1, v2));
};

const findClosestPointIndex = (track: TrackPoint[], point: TrackPoint, after = 0) => {
  let index = after;
  let min = dist(track[after], point);
  for (let i = after; i < track.length; i++) {
    if (min > dist(track[i], point)) {
      min = dist(track[i], point);
      index = i;
    }
  }
  return index;
};

const findPointIndexByTime = (track: TrackPoint[], time: number) => {
  let index = 0;
  let minTime = MAX_TIME;
  for (let i = 0; i < track.length && track[i].time <= time; i++) {
    if (minTime > time - track[i].time) {
      minTime = time - track[i].time;
      index = i;
    }
  }
  return index;
};

const toRadian = (degree: number) => {
  return (degree * Math.PI) / 180;
};

const getDistance = (origin: LatLngLiteral, destination: LatLngLiteral) => {
  // return distance in meters
  let lon1 = toRadian(origin.lng);
  let lat1 = toRadian(origin.lat);
  let lon2 = toRadian(destination.lng);
  let lat2 = toRadian(destination.lat);

  var deltaLat = lat2 - lat1;
  var deltaLon = lon2 - lon1;

  var a = Math.pow(Math.sin(deltaLat / 2), 2) + Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(deltaLon / 2), 2);
  var c = 2 * Math.asin(Math.sqrt(a));
  var EARTH_RADIUS = 6371;
  return c * EARTH_RADIUS * 1000;
};

const checkOtherClosePoint = (track: TrackPoint[], position: LatLngLiteral, index: number) => {
  for (let i = 0; i < track.length; i++) {
    if (getDistance(track[i], position) <= MAX_RADIUS) {
      if (Math.abs(track[i].time - track[index].time) > MAX_TIME) {
        return false;
      }
    }
  }
  return true;
};

const findSIControlTime = (siData: SiDataType, code: number) => {
  if (siData && code) {
    if (code === 1 && siData.start) return new Date(siData.start).getTime();
    if (code === 1000 && siData.finish) return new Date(siData.finish).getTime();
    const si = siData.controls.find((s) => s.code === code);
    return si ? new Date(si.time).getTime() : null;
  } else {
    return null;
  }
};

const findClearPoints = (track: TrackPoint[], siData: SiDataType, checkpoints: ControlType[]) => {
  const points = [];
  let last = 0;
  for (let i = 0; i < checkpoints.length; i++) {
    const siControlTime = findSIControlTime(siData, checkpoints[i].siCode);
    if (siControlTime) {
      const index = findPointIndexByTime(track, siControlTime);
      points[i] = index;
      last = index;
    } else {
      const index = findClosestPointIndex(track, checkpoints[i].position, last);
      if (getDistance(checkpoints[i].position, track[index]) < MAX_RADIUS && checkOtherClosePoint(track, checkpoints[i].position, index)) {
        points[i] = index;
        last = index;
      } else {
        points[i] = -1;
      }
    }
  }

  let first = 0;
  for (let i = 0; i < checkpoints.length; i++) {
    if (points[i] !== -1) {
      first = points[i];
    } else {
      let j = i;
      while (j < checkpoints.length && points[j] === -1) {
        j++;
      }
      let parcialTrack: TrackPoint[] = j < checkpoints.length ? track.slice(first, points[j]) : track.slice(first);
      if (parcialTrack.length !== 0) {
        points[i] = findClosestPointIndex(parcialTrack, checkpoints[i].position) + first;
      } else {
        points[i] = points[i - 1];
      }
    }
  }
  return points;
};

const calculatePlayer = (player: CompetitorType, checkpoints: ControlType[]) => {
  const points = findClearPoints(player.track, player.siData, checkpoints);

  player.splits = [];
  for (let i = 0; i < points.length - 1; i++) {
    const track = player.track.slice(points[i], points[i + 1] + 1);
    let time = 0;
    let distance = 0;
    if (track.length > 0) {
      const begin = findSIControlTime(player.siData, checkpoints[i].siCode) || track[0].time;
      const end = findSIControlTime(player.siData, checkpoints[i + 1].siCode) || track[track.length - 1].time;
      time = (end - begin) / 1000;
      for (let i = 1; i < track.length; i++) {
        distance += getDistance(track[i - 1], track[i]);
      }
      distance = Math.round(distance);
    }

    player.splits[i] = {
      track,
      time,
      distance
    };
  }
  player.display = {track: [], color: player.color};
  return player;
};

const calculatePlayers = (players: CompetitorType[], checkpoints: ControlType[]) => {
  return players.map((player) => calculatePlayer({...player}, checkpoints));
};

export {interpolationPlayerTrack, findClosestPointIndex, getDistance, calculatePlayers};
